import React, { useState, useMemo } from 'react';
import L from 'leaflet';

const ReportZone = ({ 
    zones, 
    setZones, 
    toggleVisibility, 
    zoomToFeature,
    handleColorChange // Passed from ReportNew as handleZoneColorChange
}) => {
    const [footerUnit, setFooterUnit] = useState('sqft');

    // --- AREA CALCULATION LOGIC ---
    const calculateArea = (coords) => {
    if (!coords || coords.length === 0) return { sqft: 0, acre: 0 };

    // Handle multi-ring: [[ring1], [ring2]] or flat [[[lng,lat],...]]
    // Get the outer ring only
    let ring = coords;
    if (Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
        ring = coords[0]; // first ring of multi-ring polygon
    }

    if (!ring || ring.length < 3) return { sqft: 0, acre: 0 };

    // Mapbox coords are [lng, lat] — use spherical excess formula
    const RADIUS = 6378137;
    let area = 0;

    for (let i = 0; i < ring.length; i++) {
        const p1 = ring[i];
        const p2 = ring[(i + 1) % ring.length];
        const lng1 = p1[0] * Math.PI / 180;
        const lng2 = p2[0] * Math.PI / 180;
        const lat1 = p1[1] * Math.PI / 180;
        const lat2 = p2[1] * Math.PI / 180;
        area += (lng2 - lng1) * (2 + Math.sin(lat1) + Math.sin(lat2));
    }

    const areaInSqMeters = Math.abs(area * RADIUS * RADIUS / 2.0);

    return {
    sqft: parseFloat((areaInSqMeters * 10.7639).toFixed(2)),
    acre: parseFloat((areaInSqMeters / 4046.86).toFixed(2))
};
};

    // Enrich zones with calculated area
    const enrichedZones = useMemo(() => {
    return zones; // sqft and acre already set correctly in ReportNew
}, [zones]);

    const totals = useMemo(() => {
        const totalSqft = enrichedZones.reduce((acc, curr) => acc + curr.sqft, 0);
        const totalAcre = enrichedZones.reduce((acc, curr) => acc + curr.acre, 0);
        return { 
            sqft: Math.round(totalSqft).toLocaleString(), 
            acre: totalAcre.toFixed(3) 
        };
    }, [enrichedZones]);

    const allVisible = zones.length > 0 && zones.every(z => z.visible);

    const handleGlobalUnitToggle = () => {
        setFooterUnit(prev => prev === 'sqft' ? 'acre' : 'sqft');
    };

    const toggleAllZones = (visible) => {
        zones.forEach(z => {
            if (z.visible !== visible) toggleVisibility(z.id, 'zone');
        });
    };

    return (
        <div className="coverage-section" id="zones-section">
            <div className="coverage-header">
                <div className="coverage-header-left">
                    <i className="fas fa-layer-group" />
                    <span>Zones Coverage</span>
                </div>

                <div className="total-badge">
                    <i className="fas fa-calculator" />
                    <span>{zones.length} Zones</span>
                </div>

                <div className="coverage-header-right">
                    <button className="section-unit-toggle" onClick={handleGlobalUnitToggle} title="Switch all units">
                        <i className="fas fa-exchange-alt" />
                    </button>
                    <button 
                        className={`section-eye-toggle ${allVisible ? 'active' : ''}`} 
                        onClick={() => toggleAllZones(!allVisible)}
                    >
                        <i className={`fas ${allVisible ? 'fa-eye' : 'fa-eye-slash'}`} />
                    </button>
                </div>
            </div>

            <table className="coverage-table">
                <tbody>
                    {enrichedZones.map((zone) => (
                        <tr key={zone.id}>
                            <td className="feature-name-cell">
                                <div className="feature-left">
                                    {/* Color Box with Hidden Input for Picker */}
                                    <div className="feature-color-box" style={{ background: zone.color, position: 'relative' }}>
                                        <input 
                                            type="color" 
                                            value={zone.color} 
                                            onChange={(e) => handleColorChange(zone.id, e.target.value)}
                                            style={{ opacity: 0, position: 'absolute', inset: 0, cursor: 'pointer', width: '100%', height: '100%' }}
                                        />
                                    </div>
                                    <div className="feature-name">{zone.name}</div>
                                </div>
                            </td>
                            <td className="feature-value-cell">
                                <div className="feature-right">
                                    <div className="feature-value-container">
                                        <span className="feature-value">
                                            {footerUnit === 'sqft' ? Math.round(zone.sqft).toLocaleString() : zone.acre.toFixed(2)}
                                        </span>
                                        <span className="feature-unit">{footerUnit === 'sqft' ? 'sq ft' : 'acre'}</span>
                                    </div>

                                    {/* Zoom to specific zone */}
                                    <button 
                                        className="eye-toggle-small" 
                                        onClick={() => zoomToFeature(zone.coords)}
                                        title="Jump to Zone"
                                    >
                                        <i className="fas fa-crosshairs" />
                                    </button>

                                    {/* Visibility Toggle */}
                                    <button 
                                        className={`eye-toggle-small ${zone.visible ? 'active' : ''}`}
                                        onClick={() => toggleVisibility(zone.id, 'zone')}
                                    >
                                        <i className={`fas ${zone.visible ? 'fa-eye' : 'fa-eye-slash'}`} />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="total-area-row">
                <div className="total-area-content">
                    <div className="total-area-label"><i className="fas fa-calculator" /> Total Zone Area</div>
                    <div className="total-area-value">
                        <span className="total-value">{footerUnit === 'sqft' ? totals.sqft : totals.acre}</span>
                        <span className="total-unit">{footerUnit === 'sqft' ? 'sq ft' : 'acre'}</span>
                        <button className="total-unit-toggle" onClick={handleGlobalUnitToggle}>
                            <i className="fas fa-exchange-alt" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportZone;